import { useCallback, useEffect, useState } from 'react'
import { getCurrentPeriod, generatePeriod } from '@/utils/periodHelpers'

interface StoredPeriodSelection {
  period: string
  timestamp: number
}

interface UsePeriodSelectionReturn {
  suggestedPeriod: string | null
  savePeriodSelection: (period: string) => void
}

/**
 * Custom hook for intelligent period selection with localStorage persistence
 *
 * Features:
 * - Remembers last selected period with timestamp
 * - Auto-advances to previous month when month rolls over (payroll lag pattern)
 * - Validates suggestions against available periods
 * - Graceful fallback chain: stored → previous month → current month → first available
 *
 * @param storageKey - Unique localStorage key for this context (e.g., 'upload-workflow-last-period')
 * @param availablePeriods - Array of valid period strings in format "yyyy-MM-dd"
 * @returns Object with suggestedPeriod and savePeriodSelection function
 */
export function usePeriodSelection(
  storageKey: string,
  availablePeriods: string[]
): UsePeriodSelectionReturn {
  const [suggestedPeriod, setSuggestedPeriod] = useState<string | null>(null)

  // Calculate suggested period based on stored data and current date
  useEffect(() => {
    if (availablePeriods.length === 0) {
      setSuggestedPeriod(null)
      return
    }

    try {
      const stored = localStorage.getItem(storageKey)
      const currentPeriod = getCurrentPeriod()
      const now = new Date()
      const previousMonth = generatePeriod(now.getFullYear(), now.getMonth()) // getMonth() is 0-indexed, so this is previous month

      let suggestion: string | null = null

      if (stored) {
        const data: StoredPeriodSelection = JSON.parse(stored)
        const storedDate = new Date(data.timestamp)

        // If we're still in the same month as when the period was selected, use stored period
        if (
          storedDate.getFullYear() === now.getFullYear() &&
          storedDate.getMonth() === now.getMonth() &&
          availablePeriods.includes(data.period)
        ) {
          suggestion = data.period
        } else {
          // Month has rolled over, suggest previous month (payroll lag pattern)
          suggestion = availablePeriods.includes(previousMonth) ? previousMonth : null
        }
      }

      // Fallback chain if no suggestion yet
      if (!suggestion) {
        // Try previous month (most common for payroll data)
        if (availablePeriods.includes(previousMonth)) {
          suggestion = previousMonth
        }
        // Try current month
        else if (availablePeriods.includes(currentPeriod)) {
          suggestion = currentPeriod
        }
        // Last resort: first available period
        else {
          suggestion = availablePeriods[0]
        }
      }

      setSuggestedPeriod(suggestion)
    } catch (error) {
      console.warn('Failed to load period selection from localStorage:', error)

      // Fallback to previous month or first available
      const now = new Date()
      const previousMonth = generatePeriod(now.getFullYear(), now.getMonth())

      if (availablePeriods.includes(previousMonth)) {
        setSuggestedPeriod(previousMonth)
      } else {
        setSuggestedPeriod(availablePeriods[0])
      }
    }
  }, [storageKey, availablePeriods])

  // Save period selection with current timestamp
  const savePeriodSelection = useCallback((period: string) => {
    try {
      const data: StoredPeriodSelection = {
        period,
        timestamp: Date.now()
      }
      localStorage.setItem(storageKey, JSON.stringify(data))
    } catch (error) {
      console.warn('Failed to save period selection to localStorage:', error)
    }
  }, [storageKey])

  return {
    suggestedPeriod,
    savePeriodSelection
  }
}
